import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import type { PortalStatementEntry } from '../../types';
import { scheduleBilledGroupLines, scheduleMilestoneDates, type StatementChangeOrderLine, type StatementScheduleRow } from '../../utils/portalStatement';

const styles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 56,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#1a1a1a',
    backgroundColor: '#ffffff',
  },
  repeatHead: {
    position: 'absolute',
    top: 36,
    left: 40,
    right: 40,
  },
  banner: {
    backgroundColor: '#2C3E2F',
    padding: 18,
    borderRadius: 8,
    marginBottom: 20,
    alignItems: 'center',
  },
  logo: { width: 210, height: 'auto', marginBottom: 12 },
  companyName: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 4,
  },
  eyebrow: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 9,
    letterSpacing: 1.2,
    marginBottom: 10,
    textAlign: 'center',
  },
  projectLine: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'baseline',
  },
  projectNumber: {
    color: '#ffffff',
    fontSize: 13,
    fontFamily: 'Helvetica-Bold',
    marginRight: 8,
  },
  projectName: {
    color: 'rgba(255,255,255,0.92)',
    fontSize: 13,
  },
  metaRow: { flexDirection: 'row', marginBottom: 5 },
  metaLabel: { fontSize: 10, color: '#64748b', width: 110 },
  metaValue: { fontSize: 10, color: '#0f172a', fontFamily: 'Helvetica-Bold', flex: 1 },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#2C3E2F',
    marginTop: 16,
    marginBottom: 8,
  },
  scheduleTitle: { marginTop: 0 },
  schedulePagePad: { paddingTop: 70 },
  tableHead: {
    flexDirection: 'row',
    backgroundColor: '#2C3E2F',
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  th: { color: '#ffffff', fontSize: 8, fontFamily: 'Helvetica-Bold' },
  row: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
  },
  rowAlt: { backgroundColor: '#f8fafc' },
  scheduleBlock: {
    borderBottomWidth: 0.5,
    borderBottomColor: '#e2e8f0',
  },
  rowPlain: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  foldRow: {
    flexDirection: 'row',
    paddingBottom: 4,
    paddingHorizontal: 6,
  },
  foldLabel: { width: '30%', fontSize: 7, color: '#475569', paddingLeft: 8 },
  foldAmount: { width: '12%', fontSize: 7, color: '#475569', textAlign: 'right' },
  foldInvoiced: { width: '12%', fontSize: 8, color: '#0f172a', textAlign: 'right' },
  foldAfterInvoiced: { width: '24%' },
  foldSpacer: { width: '58%' },
  td: { fontSize: 8, color: '#0f172a' },
  tdMuted: { fontSize: 7, color: '#64748b', marginTop: 1 },
  installmentLine: { flexDirection: 'row', alignItems: 'center' },
  statusBadge: {
    marginLeft: 5,
    paddingVertical: 1.5,
    paddingHorizontal: 4,
    borderRadius: 3,
  },
  statusBadgeText: { fontSize: 6, fontFamily: 'Helvetica-Bold' },
  colInstallment: { width: '18%' },
  colWhen: { width: '12%' },
  colMoney: { width: '12%', textAlign: 'right', paddingRight: 4 },
  colDates: { width: '22%', paddingLeft: 8 },
  dateCaption: { fontSize: 6, color: '#64748b' },
  dateValue: { fontSize: 7, color: '#0f172a' },
  dateGap: { marginTop: 2 },
  colAct: { width: '46%' },
  colDate: { width: '18%' },
  colCharge: { width: '18%', textAlign: 'right' },
  colPay: { width: '18%', textAlign: 'right' },
  coNumber: { width: '34%' },
  coTerms: { width: '42%' },
  coAmount: { width: '24%', textAlign: 'right' },
  totalsBox: { marginTop: 14, alignSelf: 'flex-end', width: 260 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 },
  totalLabel: { fontSize: 10, color: '#475569' },
  totalValue: { fontSize: 10, color: '#0f172a', fontFamily: 'Helvetica-Bold' },
  grandRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 8,
    marginTop: 4,
    borderTopWidth: 2,
    borderTopColor: '#2C3E2F',
  },
  grandLabel: { fontSize: 12, color: '#2C3E2F', fontFamily: 'Helvetica-Bold' },
  grandValue: { fontSize: 12, color: '#2C3E2F', fontFamily: 'Helvetica-Bold' },
  note: { fontSize: 8, color: '#64748b', marginTop: 10, lineHeight: 1.4 },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: { fontSize: 8, color: '#94a3b8' },
});

function money(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return '';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function signedMoney(value: number): string {
  const formatted = money(value);
  return value > 0 ? `+${formatted}` : formatted;
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '';
  return new Date(parsed).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function longDate(date: Date): string {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** Keep "When" phrases wrapping on whole words. */
function keepWholeWord(word: string): string[] {
  return [word];
}

function statusBadgeColors(label: string): { backgroundColor: string; color: string } {
  if (label === 'Paid') return { backgroundColor: '#dce8dc', color: '#1e3a24' };
  if (label === 'Invoiced' || label === 'Partial') return { backgroundColor: '#e4ebf5', color: '#1e3a5f' };
  if (label === 'Partially paid') return { backgroundColor: '#f6edd9', color: '#6b4e16' };
  return { backgroundColor: '#eef2f4', color: '#52606d' };
}

function ScheduleDates(props: { invoicedAt?: string; paidAt?: string }) {
  return (
    <View style={styles.colDates}>
      {props.invoicedAt ? (
        <View>
          <Text style={styles.dateCaption}>Invoiced</Text>
          <Text style={styles.dateValue}>{formatDate(props.invoicedAt)}</Text>
        </View>
      ) : null}
      {props.paidAt ? (
        <View style={props.invoicedAt ? styles.dateGap : undefined}>
          <Text style={styles.dateCaption}>Paid</Text>
          <Text style={styles.dateValue}>{formatDate(props.paidAt)}</Text>
        </View>
      ) : null}
    </View>
  );
}

function ScheduleColumns() {
  return (
    <>
      <Text style={[styles.th, styles.colInstallment]}>Installment</Text>
      <Text style={[styles.th, styles.colWhen]}>When</Text>
      <Text style={[styles.th, styles.colMoney]}>Amount</Text>
      <Text style={[styles.th, styles.colMoney]}>Invoiced</Text>
      <Text style={[styles.th, styles.colMoney]}>Paid</Text>
      <Text style={[styles.th, styles.colMoney]}>Remaining</Text>
      <Text style={[styles.th, styles.colDates]}>Dates</Text>
    </>
  );
}

export interface PortalStatementPdfProps {
  companyLogo?: string;
  companyName?: string;
  companyWebsite?: string;
  projectName: string;
  /** Client company, shown as the header. */
  clientCompany?: string;
  clientName?: string;
  projectNumber?: string | null;
  generatedAt?: Date;
  originalContract: number;
  changeOrderTotal: number;
  currentContractValue: number;
  changeOrders?: StatementChangeOrderLine[];
  schedule: StatementScheduleRow[];
  activity: PortalStatementEntry[];
}

export function PortalStatementPdfDocument(props: PortalStatementPdfProps) {
  const generatedAt = props.generatedAt || new Date();
  const companyName = props.companyName || "Leiper's Creek Timber Frames";
  const companyWebsite = props.companyWebsite || 'www.lctimberframes.com';
  const paid = props.schedule.reduce((sum, row) => sum + row.paid, 0);
  const activityPaid = props.activity
    .filter((line) => line.kind !== 'design_fee')
    .reduce((sum, line) => sum + Math.round(line.payment || 0), 0);
  const paidToDate = Math.max(paid, activityPaid);
  const balance = Math.max(0, Math.round(props.currentContractValue) - paidToDate);
  const activity = props.activity || [];
  const changeOrders = props.changeOrders || [];
  const clientCompany = (props.clientCompany || '').trim();
  const clientName = (props.clientName || '').trim();
  const headerCompany = clientCompany || clientName || 'Client';

  const footer = (
    <View style={styles.footer} fixed>
      <Text style={styles.footerText}>{companyName} • {companyWebsite}</Text>
      <Text
        style={styles.footerText}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  );

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.banner}>
          {props.companyLogo ? <Image src={props.companyLogo} style={styles.logo} /> : null}
          <Text style={styles.companyName}>{headerCompany}</Text>
          <Text style={styles.eyebrow}>STATEMENT OF ACCOUNT</Text>
          <View style={styles.projectLine}>
            {props.projectNumber ? <Text style={styles.projectNumber}>{props.projectNumber}</Text> : null}
            <Text style={styles.projectName}>{props.projectName}</Text>
          </View>
        </View>

        {clientName && clientName !== headerCompany ? (
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Prepared for</Text>
            <Text style={styles.metaValue}>{clientName}</Text>
          </View>
        ) : null}
        <View style={styles.metaRow}>
          <Text style={styles.metaLabel}>Statement date</Text>
          <Text style={styles.metaValue}>{longDate(generatedAt)}</Text>
        </View>

        <Text style={styles.sectionTitle}>Contract</Text>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Original contract</Text>
          <Text style={styles.totalValue}>{money(props.originalContract)}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Change orders</Text>
          <Text style={styles.totalValue}>
            {props.changeOrderTotal > 0 ? '+' : ''}{money(props.changeOrderTotal)}
          </Text>
        </View>
        <View style={styles.grandRow}>
          <Text style={styles.grandLabel}>Current contract value</Text>
          <Text style={styles.grandValue}>{money(props.currentContractValue)}</Text>
        </View>

        {changeOrders.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Change Orders</Text>
            <View style={styles.tableHead}>
              <Text style={[styles.th, styles.coNumber]}>Number</Text>
              <Text style={[styles.th, styles.coTerms]}>Terms</Text>
              <Text style={[styles.th, styles.coAmount]}>Amount</Text>
            </View>
            {changeOrders.map((co, index) => (
              <View key={co.id} style={index % 2 === 1 ? [styles.row, styles.rowAlt] : styles.row}>
                <View style={styles.coNumber}>
                  <Text style={styles.td}>{co.numberLabel}</Text>
                  {co.title ? <Text style={styles.tdMuted}>{co.title}</Text> : null}
                </View>
                <Text style={[styles.td, styles.coTerms]}>{co.terms}</Text>
                <Text style={[styles.td, styles.coAmount]}>{signedMoney(co.amount)}</Text>
              </View>
            ))}
          </>
        )}

        {footer}
      </Page>

      <Page size="LETTER" style={[styles.page, styles.schedulePagePad]}>
        <View
          fixed
          style={styles.repeatHead}
          render={(info) => {
            const subPageNumber = (info as { subPageNumber?: number }).subPageNumber ?? 1;
            if (subPageNumber <= 1) return null;
            return (
              <View style={styles.tableHead}>
                <ScheduleColumns />
              </View>
            );
          }}
        />
        <Text style={[styles.sectionTitle, styles.scheduleTitle]}>Payment schedule</Text>
        <View style={styles.tableHead}>
          <ScheduleColumns />
        </View>
        {props.schedule.map((row, index) => {
          const folded = row.kind === 'change_order' ? [] : (row.folded || []);
          const billedGroups = scheduleBilledGroupLines(row, props.activity);
          const milestoneDates = billedGroups.length ? {} : scheduleMilestoneDates(row, props.activity);
          return (
            <View
              key={row.id}
              wrap={false}
              style={index % 2 === 1 ? [styles.scheduleBlock, styles.rowAlt] : styles.scheduleBlock}
            >
              <View style={styles.rowPlain}>
                <View style={styles.colInstallment}>
                  <View style={styles.installmentLine}>
                    <Text style={styles.td}>{row.label}</Text>
                    {row.statusLabel ? (
                      <View style={[styles.statusBadge, { backgroundColor: statusBadgeColors(row.statusLabel).backgroundColor }]}>
                        <Text style={[styles.statusBadgeText, { color: statusBadgeColors(row.statusLabel).color }]}>
                          {row.statusLabel}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                </View>
                <Text style={[styles.td, styles.colWhen]} hyphenationCallback={keepWholeWord}>{row.detail}</Text>
                <Text style={[styles.td, styles.colMoney]}>{money(row.amount)}</Text>
                <Text style={[styles.td, styles.colMoney]}>{billedGroups.length ? ' ' : money(row.invoiced)}</Text>
                <Text style={[styles.td, styles.colMoney]}>{money(row.paid)}</Text>
                <Text style={[styles.td, styles.colMoney]}>{money(row.remaining)}</Text>
                <ScheduleDates invoicedAt={milestoneDates.invoicedAt} paidAt={milestoneDates.paidAt} />
              </View>
              {folded.length > 0 && row.baseAmount != null && (
                <View style={styles.foldRow}>
                  <Text style={styles.foldLabel}>Base</Text>
                  <Text style={styles.foldAmount}>{money(row.baseAmount)}</Text>
                  <Text style={styles.foldSpacer}> </Text>
                </View>
              )}
              {folded.map((line, foldIndex) => (
                <View key={`${row.id}-${line.reference}-${foldIndex}`} style={styles.foldRow}>
                  <Text style={styles.foldLabel}>{line.reference} folded in</Text>
                  <Text style={styles.foldAmount}>{signedMoney(line.amount)}</Text>
                  <Text style={styles.foldSpacer}> </Text>
                </View>
              ))}
              {billedGroups.map((group, groupIndex) => (
                <View key={`${row.id}-billed-${groupIndex}`} style={styles.foldRow}>
                  <Text style={styles.foldLabel}>{group.label}</Text>
                  <Text style={styles.foldAmount}> </Text>
                  <Text style={styles.foldInvoiced}>{money(group.invoiced)}</Text>
                  <Text style={styles.foldAfterInvoiced}> </Text>
                  <ScheduleDates invoicedAt={group.invoicedAt} paidAt={group.paidAt} />
                </View>
              ))}
            </View>
          );
        })}

        {activity.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Invoices and payments</Text>
            <View style={styles.tableHead}>
              <Text style={[styles.th, styles.colDate]}>Date</Text>
              <Text style={[styles.th, styles.colAct]}>Description</Text>
              <Text style={[styles.th, styles.colCharge]}>Charge</Text>
              <Text style={[styles.th, styles.colPay]}>Payment</Text>
            </View>
            {activity.map((line, index) => (
              <View key={line.id} style={index % 2 === 1 ? [styles.row, styles.rowAlt] : styles.row}>
                <Text style={[styles.td, styles.colDate]}>{formatDate(line.date)}</Text>
                <View style={styles.colAct}>
                  <Text style={styles.td}>{line.label}</Text>
                  {line.detail ? <Text style={styles.tdMuted}>{line.detail}</Text> : null}
                  {line.quickbooksInvoiceNum ? (
                    <Text style={styles.tdMuted}>Invoice #{line.quickbooksInvoiceNum}</Text>
                  ) : null}
                </View>
                <Text style={[styles.td, styles.colCharge]}>{line.charge ? money(line.charge) : ''}</Text>
                <Text style={[styles.td, styles.colPay]}>{line.payment ? money(line.payment) : ''}</Text>
              </View>
            ))}
          </>
        )}

        <View style={styles.totalsBox} wrap={false}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Paid on the contract</Text>
            <Text style={styles.totalValue}>{money(paidToDate)}</Text>
          </View>
          <View style={styles.grandRow}>
            <Text style={styles.grandLabel}>Balance remaining</Text>
            <Text style={styles.grandValue}>{money(balance)}</Text>
          </View>
        </View>
        <Text style={styles.note}>
          This statement is the current status of the signed contract. Amounts not yet invoiced remain on the payment schedule until that installment is billed. The customer bill is the QuickBooks invoice.
        </Text>

        {footer}
      </Page>
    </Document>
  );
}
